import { kebabCase } from 'lodash-es';
import { type TableHighlightingMode } from '@components/floorPlan/FloorPlanTable';
import type {
  HostFloorPlanTable,
  TableTimer,
} from 'restaurantAdmin/floorPlans/apiHelpers';
import { useServiceSearchParams } from '../hooks/useServiceSearchParams';
import { useReservationServiceContext } from '../state/ReservationServiceContext';
import { SeatedFloorPlanTable } from './SeatedFloorPlanTable';
import { UnoccupiedFloorPlanTable } from './UnoccupiedFloorPlanTable';

interface OccupantFloorPlanTableFactoryProps {
  floorPlanTable: HostFloorPlanTable;
  handleTableOnClick: (floorPlanTable: HostFloorPlanTable) => void;
  listingIdsToPulsate?: string[];
  selectedEmptyTableId: string | undefined;
  timer?: TableTimer | null;
  tableIconScale: number;
}
export const OccupantFloorPlanTableFactory = ({
  floorPlanTable,
  handleTableOnClick,
  listingIdsToPulsate,
  selectedEmptyTableId,
  timer,
  tableIconScale,
}: OccupantFloorPlanTableFactoryProps) => {
  const { openOccupantDetailsDrawer } = useServiceSearchParams();
  const { seatedOccupant } = floorPlanTable;
  const { selectedOccupant } = useReservationServiceContext();
  const isSeated = seatedOccupant !== null;
  const testId = kebabCase(`floor-plan-table-${floorPlanTable.name}`);

  if (isSeated) {
    return (
      <SeatedFloorPlanTable
        timer={timer}
        handleTableOnClick={() => {
          openOccupantDetailsDrawer(seatedOccupant);
        }}
        iconName={floorPlanTable.iconName}
        iconWidthScale={floorPlanTable.iconWidthScale}
        isAnyTableHighlighted={selectedOccupant != null}
        isDisabled={false}
        isHighlighted={selectedOccupant?.id === seatedOccupant.id}
        key={floorPlanTable.id}
        left={floorPlanTable.left}
        name={floorPlanTable.name}
        orientation={floorPlanTable.orientation}
        seatedOccupant={seatedOccupant}
        tableIconScale={tableIconScale}
        testId={testId}
        top={floorPlanTable.top}
        assignedServer={floorPlanTable.assignedServer}
      />
    );
  }

  const isSelectedTable = selectedEmptyTableId
    ? selectedEmptyTableId === floorPlanTable.id
    : false;

  const isHighlighted =
    isSelectedTable ||
    floorPlanTable.listings.some((l) => listingIdsToPulsate?.includes(l.id));

  const tableHighlightingMode: TableHighlightingMode =
    listingIdsToPulsate?.length && listingIdsToPulsate.length > 0
      ? 'pulse'
      : 'outline';

  return (
    <UnoccupiedFloorPlanTable
      timer={timer}
      handleTableOnClick={() => {
        // This is still necessary to trigger the "SeatWalkIn" side sheet to
        // appear. Once we move that into a drawer we can remove this and update
        // searchParams instead.
        void handleTableOnClick(floorPlanTable);
      }}
      iconName={floorPlanTable.iconName}
      iconWidthScale={floorPlanTable.iconWidthScale}
      isHighlighted={isHighlighted}
      isSelected={isSelectedTable}
      key={floorPlanTable.id}
      left={floorPlanTable.left}
      name={floorPlanTable.name}
      orientation={floorPlanTable.orientation}
      tableHighlightingMode={tableHighlightingMode}
      tableIconScale={tableIconScale}
      testId={testId}
      top={floorPlanTable.top}
      assignedServer={floorPlanTable.assignedServer}
    />
  );
};
